import { supabase } from "./supabase";
import type {
  UserStats,
  BadgeDefinition,
  UserBadge,
  SupplementFact,
} from "./supabase";

export async function getUserStats(): Promise<UserStats | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from("user_stats")
    .select("*")
    .eq("user_id", user.id)
    .maybeSingle();

  if (error && error.code !== "PGRST116") {
    console.error("Error fetching user stats:", error);
    return null;
  }

  if (!data) {
    const { data: newStats, error: createError } = await supabase
      .from("user_stats")
      .insert({
        user_id: user.id,
        current_streak_days: 0,
        longest_streak_days: 0,
        total_doses_taken: 0,
        perfect_days_count: 0,
        facts_viewed_count: 0,
      })
      .select()
      .single();

    if (createError) {
      console.error("Error creating user stats:", createError);
      return null;
    }

    return newStats;
  }

  return data;
}

export async function updateUserStats(
  updates: Partial<UserStats>
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from("user_stats")
    .update(updates)
    .eq("user_id", user.id);

  if (error) {
    console.error("Error updating user stats:", error);
  }
}

export async function incrementDoseTaken(count: number = 1): Promise<void> {
  const stats = await getUserStats();
  if (!stats) return;

  console.log(`💊 Incrementing dose count by ${count}`);

  await updateUserStats({
    total_doses_taken: stats.total_doses_taken + count,
  });

  // Check milestone badges after updating dose count
  await checkMilestoneBadges();
}

export async function checkAndUpdateStreak(): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  console.log("🎯 Checking streak for user:", user.id);

  const today = new Date().toISOString().split("T")[0];
  console.log("📅 Today date:", today);

  // Get today's plan
  const { data: plan } = await supabase
    .from("day_plans")
    .select("id, wake_time")
    .eq("user_id", user.id)
    .gte("wake_time", today + "T00:00:00")
    .lt("wake_time", today + "T23:59:59")
    .order("wake_time", { ascending: false })
    .limit(1)
    .maybeSingle();

  console.log("📋 Today's plan:", plan);

  if (!plan) {
    console.log("❌ No plan found for today");
    return;
  }

  // Get all items for today's plan
  const { data: items } = await supabase
    .from("day_plan_items")
    .select("status")
    .eq("day_plan_id", plan.id);

  console.log("💊 Plan items:", items);

  if (!items || items.length === 0) {
    console.log("❌ No items found for today's plan");
    return;
  }

  const allTaken = items.every((item) => item.status === "TAKEN");
  console.log("✅ All items taken:", allTaken);

  const stats = await getUserStats();
  if (!stats) {
    console.log("❌ No user stats found");
    return;
  }

  console.log("📊 Current stats:", stats);

  if (allTaken) {
    // Check if we already processed today
    if (stats.last_perfect_day === today) {
      console.log("✅ Today already processed");
      return;
    }

    // Calculate new streak
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split("T")[0];

    let newStreak = 1;

    // If yesterday was perfect, continue the streak
    if (stats.last_perfect_day === yesterdayStr) {
      newStreak = stats.current_streak_days + 1;
      console.log("🔥 Continuing streak:", newStreak);
    } else {
      console.log("🆕 Starting new streak");
    }

    const newLongest = Math.max(newStreak, stats.longest_streak_days || 0);

    const updates = {
      current_streak_days: newStreak,
      longest_streak_days: newLongest,
      perfect_days_count: (stats.perfect_days_count || 0) + 1,
      last_perfect_day: today,
    };

    console.log("📈 Updating stats:", updates);

    await updateUserStats(updates);
    await checkStreakBadges(newStreak);

    console.log("✅ Streak updated successfully");
  } else {
    console.log("❌ Not all items taken, no streak update");
  }
}

export async function getAllBadges(): Promise<BadgeDefinition[]> {
  const { data, error } = await supabase
    .from("badge_definitions")
    .select("*")
    .order("sort_order");

  if (error) {
    console.error("Error fetching badges:", error);
    return [];
  }

  return data || [];
}

export async function getUserBadges(): Promise<UserBadge[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from("user_badges")
    .select(
      `
      *,
      badge:badge_definitions(*)
    `
    )
    .eq("user_id", user.id)
    .order("earned_at", { ascending: false });

  if (error) {
    console.error("Error fetching user badges:", error);
    return [];
  }

  return data || [];
}

export async function unlockBadge(badgeId: string): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return false;

  const existing = await supabase
    .from("user_badges")
    .select("id")
    .eq("user_id", user.id)
    .eq("badge_id", badgeId)
    .maybeSingle();

  if (existing.data) return false;

  const { error } = await supabase.from("user_badges").insert({
    user_id: user.id,
    badge_id: badgeId,
  });

  if (error) {
    console.error("Error unlocking badge:", error);
    return false;
  }

  return true;
}

async function checkStreakBadges(streakDays: number): Promise<void> {
  if (streakDays >= 7) {
    await unlockBadge("perfect_week");
  }
  if (streakDays >= 30) {
    await unlockBadge("consistency_champion");
  }
  if (streakDays >= 90) {
    await unlockBadge("iron_will");
  }
  if (streakDays >= 365) {
    await unlockBadge("health_warrior");
  }
}

export async function checkMilestoneBadges(): Promise<void> {
  const stats = await getUserStats();
  if (!stats) return;

  if (stats.total_doses_taken >= 100) {
    await unlockBadge("century_club");
  }
  if (stats.facts_viewed_count >= 50) {
    await unlockBadge("knowledge_seeker");
  }
}

export async function getRandomFact(
  excludeViewed: boolean = true
): Promise<SupplementFact | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  let query = supabase.from("supplement_facts").select("*");

  if (excludeViewed) {
    const { data: viewed } = await supabase
      .from("user_facts_viewed")
      .select("fact_id")
      .eq("user_id", user.id);

    if (viewed && viewed.length > 0) {
      const viewedIds = viewed.map((v) => v.fact_id);
      query = query.not("id", "in", `(${viewedIds.join(",")})`);
    }
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching facts:", error);
    return null;
  }

  if (!data || data.length === 0) {
    if (excludeViewed) {
      return getRandomFact(false);
    }
    return null;
  }

  const randomIndex = Math.floor(Math.random() * data.length);
  return data[randomIndex];
}

export async function markFactAsViewed(factId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  await supabase.from("user_facts_viewed").insert({
    user_id: user.id,
    fact_id: factId,
  });

  const stats = await getUserStats();
  if (stats) {
    await updateUserStats({
      facts_viewed_count: stats.facts_viewed_count + 1,
    });
    await checkMilestoneBadges();
  }
}

export async function getStreakColor(streakDays: number): string {
  if (streakDays >= 90) return "from-yellow-400 to-amber-500";
  if (streakDays >= 30) return "from-orange-500 to-red-500";
  if (streakDays >= 7) return "from-orange-400 to-orange-500";
  return "from-orange-300 to-orange-400";
}

export async function getStreakEmoji(streakDays: number): string {
  if (streakDays >= 365) return "👑";
  if (streakDays >= 90) return "🔥";
  if (streakDays >= 30) return "💪";
  if (streakDays >= 7) return "⭐";
  return "✨";
}
